import { useEffect, useRef, useState } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';

/**
 * The live evaluation trace (spec Part 1.2/Part 5). Renders only stages
 * the backend has actually entered — no line appears before its first
 * event, no percentage, no simulated increment. Hidden-case rows all
 * appear together the moment the `start` event lands and each resolves
 * independently as its own `case-result` event arrives, in whatever order
 * that actually happens (never re-sorted into a fake sequence).
 */
const STAGE_HELP = {
  generating: 'generating the output your prompt would actually produce',
  'hidden-cases': 'this checks whether your prompt holds up on inputs you have not seen',
  llm: 'an AI judge is reading your prompt against the (still-hidden) criteria',
};

const Row = ({ state, children }) => (
  <div style={{
    display: 'flex', alignItems: 'baseline', gap: 8, fontFamily: TT.fontMono, fontSize: 13,
    color: state === 'done' ? TT.textDim : TT.text,
    opacity: state === 'done' ? 0.6 : 1,
  }}>
    <span style={{ width: 14, flexShrink: 0, color: state === 'done' ? TT.green : TT.textMuted }}>
      {state === 'done' ? '✓' : '·'}
    </span>
    <span>{children}</span>
  </div>
);

/** Shows STAGE_HELP[stageKey] once, only if the stage is still pending 1.5s
 * after it started — never a rotating carousel, per Part 5. */
const useDelayedHelp = (stageKey, pending) => {
  const [show, setShow] = useState(false);
  const armedRef = useRef(false);

  useEffect(() => {
    if (!pending) { setShow(false); armedRef.current = false; return; }
    if (armedRef.current) return;
    armedRef.current = true;
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, [pending]);

  return show && STAGE_HELP[stageKey] ? STAGE_HELP[stageKey] : null;
};

const deriveView = (events) => {
  const view = {
    generating: null, generatingA: null, generatingB: null,
    structural: null,
    hiddenCases: null, // { total, cases: { [position]: passed } }
    llm: null,
  };
  for (const e of events) {
    if (e.stage === 'structural') view.structural = 'done';
    else if (e.stage === 'hidden-cases') {
      if (!view.hiddenCases) view.hiddenCases = { total: e.total ?? 3, cases: {} };
      if (e.status === 'case-result') view.hiddenCases.cases[e.position] = e.passed;
    } else if (e.stage === 'llm') view.llm = e.status;
    else if (e.stage === 'generating') view.generating = e.status;
    else if (e.stage === 'generating-a') view.generatingA = e.status;
    else if (e.stage === 'generating-b') view.generatingB = e.status;
  }
  return view;
};

export const EvaluationTrace = ({ events, isCompare }) => {
  const v = deriveView(events);
  const hiddenCaseHelp = useDelayedHelp('hidden-cases', !!v.hiddenCases && Object.keys(v.hiddenCases.cases).length < (v.hiddenCases?.total ?? 3));
  const llmHelp = useDelayedHelp('llm', v.llm === 'start');
  const genHelp = useDelayedHelp('generating', v.generating === 'start');

  if (events.length === 0) return null;

  return (
    <div style={{
      marginTop: 16, padding: '14px 16px', background: TT.bgSurface,
      border: `1px solid ${TT.border}`, borderRadius: TT.radius.sm,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.orange, marginBottom: 2 }}>$ evaluating</div>

      {isCompare ? (
        <>
          {v.generatingA && <Row state={v.generatingA}>generating output A</Row>}
          {v.generatingB && <Row state={v.generatingB}>generating output B</Row>}
        </>
      ) : (
        v.generating && (
          <>
            <Row state={v.generating}>generating your output</Row>
            {genHelp && <div style={{ fontFamily: TT.fontSans, fontSize: 11, color: TT.textDim, paddingLeft: 22 }}>{genHelp}</div>}
          </>
        )
      )}

      {v.structural && <Row state="done">structural checks</Row>}

      {v.hiddenCases && (
        <>
          <Row state={Object.keys(v.hiddenCases.cases).length === v.hiddenCases.total ? 'done' : 'pending'}>
            testing against {v.hiddenCases.total} hidden cases
          </Row>
          <div style={{ paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Array.from({ length: v.hiddenCases.total }, (_, i) => i + 1).map(pos => {
              const result = v.hiddenCases.cases[pos];
              return (
                <div key={pos} style={{ display: 'flex', gap: 8, fontFamily: TT.fontMono, fontSize: 12 }}>
                  <span style={{ color: TT.textMuted }}>case {pos}</span>
                  <span style={{ color: result == null ? TT.textDim : result ? TT.green : '#ef4444' }}>
                    {result == null ? '·' : result ? '✓' : '✗'}
                  </span>
                </div>
              );
            })}
          </div>
          {hiddenCaseHelp && <div style={{ fontFamily: TT.fontSans, fontSize: 11, color: TT.textDim, paddingLeft: 22 }}>{hiddenCaseHelp}</div>}
        </>
      )}

      {v.llm && (
        <>
          <Row state={v.llm}>judging against criteria</Row>
          {llmHelp && <div style={{ fontFamily: TT.fontSans, fontSize: 11, color: TT.textDim, paddingLeft: 22 }}>{llmHelp}</div>}
        </>
      )}
    </div>
  );
};

export default EvaluationTrace;
